import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { MetricType } from 'shared/components/metrics';
import { ErgType, GridMetricType, IntervalConfig } from 'shared/types';
import { LiveOrder, LiveViewMode, TeamDisplayMode } from 'utils/types';

interface PreferencesValue {
  defaultErgType: ErgType;
  setDefaultErgType: (ergType: ErgType) => void;
  intervalConfig: IntervalConfig;
  setIntervalConfig: (config: IntervalConfig) => void;
  showLiveIntervalMetrics: boolean;
  setShowLiveIntervalMetrics: (value: boolean) => void;
  liveMetricTypes: MetricType[];
  setLiveMetricTypes: (types: MetricType[]) => void;
  resultMetricTypes: MetricType[];
  setResultMetricTypes: (types: MetricType[]) => void;
  liveOrder: LiveOrder;
  setLiveOrder: (liveOrder: LiveOrder) => void;
  liveViewMode: LiveViewMode;
  setLiveViewMode: (liveViewMode: LiveViewMode) => void;
  liveFontScale: number;
  setLiveFontScale: (scale: number) => void;
  showLiveReactions: boolean;
  toggleLiveReactions: () => void;
  showAlwaysFullName: boolean;
  toggleShowAlwaysFullName: () => void;
  teamDisplayMode: TeamDisplayMode;
  setTeamDisplayMode: (mode: TeamDisplayMode) => void;
  liveReactionIndex: number;
  setLiveReactionIndex: (index: number) => void;
  calendarMode: boolean;
  toggleCalendarMode: () => void;
  getTableMetrics: (ergType: ErgType) => MetricType[];
  setTableMetrics: (tableMetrics: MetricType[]) => void;
  gridMetrics: GridMetricType[];
  setGridMetrics: (gridMetrics: GridMetricType[]) => void;
  lastAnnouncementDate?: string;
  setLastAnnouncementDate: (date: string) => void;
}

const PreferencesContext = createContext<PreferencesValue>(
  // @ts-ignore
  {}
);

const USER_PREFERENCES = 'USER_PREFERENCES';

interface Preferences {
  defaultErgType: ErgType;
  intervalConfig: IntervalConfig;
  liveOrder: LiveOrder;
  liveViewMode: LiveViewMode;
  showLiveIntervalMetrics: boolean;
  liveMetricTypes: MetricType[];
  resultMetricTypes: MetricType[];
  liveFontScale: number;
  showLiveReactions: boolean;
  showAlwaysFullName: boolean;
  teamDisplayMode: TeamDisplayMode;
  liveReactionIndex: number;
  calendarMode: boolean;
  tableMetrics: MetricType[];
  gridMetrics: GridMetricType[];
  lastAnnouncementDate?: string;
}

let initialPreferences: Preferences = {
  defaultErgType: ErgType.row,
  intervalConfig: {},
  liveOrder: LiveOrder.Progress,
  liveViewMode: LiveViewMode.Rows,
  showLiveIntervalMetrics: false,
  liveMetricTypes: [MetricType.diffPace],
  resultMetricTypes: [],
  liveFontScale: 100,
  showLiveReactions: true,
  showAlwaysFullName: false,
  teamDisplayMode: TeamDisplayMode.OnlyAthletes,
  liveReactionIndex: 0,
  calendarMode: false,
  lastAnnouncementDate: undefined,
  tableMetrics: [
    MetricType.time,
    MetricType.distance,
    MetricType.diffPace,
    MetricType.diffRate,
    MetricType.calories,
    MetricType.strokes,
    MetricType.dragFactor,
    MetricType.watts,
    MetricType.spi,
    MetricType.benchmark,
    MetricType.heartRate,
    MetricType.hrZone,
    MetricType.rest,
  ],
  gridMetrics: [],
};

const Store = {
  getPreferences: () => {
    const value = localStorage.getItem(USER_PREFERENCES);
    if (value) {
      let { liveOrder, ...storedValues } = JSON.parse(value) as Preferences;
      if (!Object.values(LiveOrder).includes(liveOrder)) {
        liveOrder = initialPreferences.liveOrder;
      }
      return { ...initialPreferences, ...storedValues, liveOrder };
    } else {
      return initialPreferences;
    }
  },
  setPreferences: (preferences: Preferences) =>
    localStorage.setItem(USER_PREFERENCES, JSON.stringify(preferences)),
};

export const PreferencesProvider = (props: any) => {
  const [preferences, setPreferences] = useState(() => {
    const stored = Store.getPreferences();
    initialPreferences = stored;
    return stored;
  });

  const savePreferences = useCallback((newPreferences: Preferences) => {
    initialPreferences = newPreferences;
    setPreferences(newPreferences);
    Store.setPreferences(newPreferences);
  }, []);

  useEffect(() => {
    const preferences = Store.getPreferences();
    initialPreferences = preferences;
    setPreferences(preferences);
  }, []);

  const {
    defaultErgType,
    intervalConfig,
    liveOrder,
    liveViewMode,
    liveMetricTypes,
    resultMetricTypes,
    showLiveIntervalMetrics,
    liveFontScale,
    showLiveReactions,
    showAlwaysFullName,
    teamDisplayMode,
    liveReactionIndex,
    calendarMode,
    tableMetrics,
    gridMetrics,
    lastAnnouncementDate,
  } = preferences;

  const setDefaultErgType = useCallback(
    (defaultErgType: ErgType) => {
      savePreferences({ ...initialPreferences, defaultErgType });
    },
    [savePreferences]
  );

  const setIntervalConfig = useCallback(
    (config: IntervalConfig) => {
      savePreferences({ ...initialPreferences, intervalConfig: config });
    },
    [savePreferences]
  );

  const setLiveOrder = useCallback(
    (liveOrder: LiveOrder) => {
      savePreferences({ ...initialPreferences, liveOrder });
    },
    [savePreferences]
  );

  const setLiveViewMode = useCallback(
    (liveViewMode: LiveViewMode) => {
      savePreferences({ ...initialPreferences, liveViewMode });
    },
    [savePreferences]
  );

  const setShowLiveIntervalMetrics = useCallback(
    (value: boolean) => {
      savePreferences({
        ...initialPreferences,
        showLiveIntervalMetrics: value,
      });
    },
    [savePreferences]
  );

  const setLiveMetricTypes = useCallback(
    (types: MetricType[]) => {
      savePreferences({
        ...initialPreferences,
        liveMetricTypes: types,
      });
    },
    [savePreferences]
  );

  const setResultMetricTypes = useCallback(
    (types: MetricType[]) => {
      savePreferences({
        ...initialPreferences,
        resultMetricTypes: types,
      });
    },
    [savePreferences]
  );

  const setLiveFontScale = useCallback(
    (liveFontScale: number) => {
      savePreferences({ ...initialPreferences, liveFontScale });
    },
    [savePreferences]
  );

  const toggleLiveReactions = useCallback(() => {
    savePreferences({
      ...initialPreferences,
      showLiveReactions: !initialPreferences.showLiveReactions,
    });
  }, [savePreferences]);

  const toggleShowAlwaysFullName = useCallback(() => {
    savePreferences({
      ...initialPreferences,
      showAlwaysFullName: !initialPreferences.showAlwaysFullName,
    });
  }, [savePreferences]);

  const setTeamDisplayMode = useCallback(
    (teamDisplayMode: TeamDisplayMode) => {
      savePreferences({ ...initialPreferences, teamDisplayMode });
    },
    [savePreferences]
  );

  const setLiveReactionIndex = useCallback(
    (liveReactionIndex: number) => {
      savePreferences({
        ...initialPreferences,
        liveReactionIndex,
      });
    },
    [savePreferences]
  );

  const toggleCalendarMode = useCallback(() => {
    savePreferences({
      ...initialPreferences,
      calendarMode: !initialPreferences.calendarMode,
    });
  }, [savePreferences]);

  const getTableMetrics = useCallback(
    (ergType: ErgType) => {
      const metrics = tableMetrics.filter(
        (metric) => metric !== MetricType.ergType
      );
      if (ergType === ErgType.multi) {
        return [MetricType.ergType, ...metrics];
      }
      return metrics;
    },
    [tableMetrics]
  );

  const setTableMetrics = useCallback(
    (metrics: MetricType[]) => {
      savePreferences({
        ...initialPreferences,
        tableMetrics: metrics.filter((metric) => metric !== MetricType.ergType),
      });
    },
    [savePreferences]
  );

  const setGridMetrics = useCallback(
    (metrics: GridMetricType[]) => {
      savePreferences({ ...initialPreferences, gridMetrics: metrics });
    },
    [savePreferences]
  );

  const setLastAnnouncementDate = useCallback(
    (lastAnnouncementDate: string) => {
      savePreferences({ ...initialPreferences, lastAnnouncementDate });
    },
    [savePreferences]
  );

  const value = {
    defaultErgType,
    setDefaultErgType,
    intervalConfig,
    setIntervalConfig,
    liveOrder,
    setLiveOrder,
    liveViewMode,
    setLiveViewMode,
    showLiveIntervalMetrics,
    setShowLiveIntervalMetrics,
    liveMetricTypes,
    setLiveMetricTypes,
    resultMetricTypes,
    setResultMetricTypes,
    liveFontScale,
    setLiveFontScale,
    showLiveReactions,
    toggleLiveReactions,
    showAlwaysFullName,
    toggleShowAlwaysFullName,
    teamDisplayMode,
    setTeamDisplayMode,
    liveReactionIndex,
    setLiveReactionIndex,
    calendarMode,
    toggleCalendarMode,
    getTableMetrics,
    setTableMetrics,
    gridMetrics,
    setGridMetrics,
    lastAnnouncementDate,
    setLastAnnouncementDate,
  };

  return <PreferencesContext.Provider value={value} {...props} />;
};

const usePreferences = () => {
  return useContext(PreferencesContext) as PreferencesValue;
};

export default usePreferences;
